import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, Send, Users, Mail, Clock, ChevronDown, ArrowLeft, Phone, Video } from "lucide-react";

export default function Messages({ student, theme, setActiveSection }) {
  const [recipients, setRecipients] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [messageStatus, setMessageStatus] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [activeTab, setActiveTab] = useState("inbox");
  const [visibleThreadsCount, setVisibleThreadsCount] = useState(5);
  
  // Conversation view states
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (conversationMessages.length > 0) {
      scrollToBottom();
    }
  }, [conversationMessages]);

  // Fetch recipients (admin and sub-admin users)
  const fetchRecipients = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin-subadmin-users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch recipients");
      
      // Filter for admin and sub-admin roles
      const filteredRecipients = data.filter(
        (user) => user.roleId == "1" || user.roleId == "2"
      );
      setRecipients(filteredRecipients);
    } catch (err) {
      console.error("Error fetching recipients:", err);
      setMessageStatus({ type: "error", message: err.message });
    }
  };

  // Fetch conversation threads
  const fetchThreads = async () => {
    try {
      setLoadingThreads(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/messages/threads/${student.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch threads");
      
      setThreads(data);
    } catch (err) {
      console.error("Error fetching threads:", err);
      setMessageStatus({ type: "error", message: err.message });
    } finally {
      setLoadingThreads(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (student?.id) {
      fetchRecipients();
      fetchThreads();
    }
  }, [student?.id]);

  // Send new message (from compose tab)
  const handleSendMessage = async () => {
    if (!selectedRecipient || !message.trim()) {
      setMessageStatus({
        type: "error",
        message: "Please select a recipient and enter a message.",
      });
      return;
    }

    try {
      setSendingMessage(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/messages/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          senderId: student.id,
          recipientId: selectedRecipient,
          message,
        }),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send message");

      setMessageStatus({
        type: "success",
        message: "Message sent successfully!",
      });
      
      // Clear form
      setMessage("");
      setSelectedRecipient("");
      
      // Refresh threads and switch to inbox
      await fetchThreads();
      setActiveTab("inbox");
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => setMessageStatus(null), 3000);
    } catch (err) {
      console.error("Error sending message:", err);
      setMessageStatus({ type: "error", message: err.message });
    } finally {
      setSendingMessage(false);
    }
  };

  // Open conversation thread
  const handleThreadClick = async (threadId) => {
    try {
      setLoadingMessages(true);
      setActiveConversation(threadId);
      
      // Fetch messages for this thread
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/messages/thread/${threadId}/${student.id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load conversation");
      
      setConversationMessages(data);
      
      // Mark thread as read
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/messages/mark-read/${threadId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ userId: student.id }),
      });
      
    } catch (err) {
      console.error("Error loading conversation:", err);
      setMessageStatus({ type: "error", message: "Failed to load conversation" });
      setActiveConversation(null);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Send reply in conversation
  const handleSendConversationMessage = async () => {
    if (!newMessage.trim() || sendingMessage) return;

    const thread = threads.find(t => t.id === activeConversation);
    if (!thread) return;

    try {
      setSendingMessage(true);
      
      // Optimistically add message to UI
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message_from: student.id,
        message_to: thread.recipient_id,
        message: newMessage,
        created_at: new Date().toISOString(),
        sender_firstName: student.firstName || "You",
        sender_lastName: student.lastName || "",
        sender_roleId: student.roleId || "3",
        recipient_firstName: thread.recipient_firstName,
        recipient_lastName: thread.recipient_lastName,
        recipient_roleId: thread.recipient_roleId
      };
      
      setConversationMessages(prev => [...prev, tempMessage]);
      const messageToSend = newMessage;
      setNewMessage("");

      // Send to API
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/messages/send-reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          thread_id: activeConversation,
          message_from: student.id,
          message_to: thread.recipient_id,
          message: messageToSend
        }),
      });
      
      const data = await res.json();
      if (!res.ok) {
        // Remove optimistic message on error
        setConversationMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
        setNewMessage(messageToSend); // Restore message text
        throw new Error(data.error || "Failed to send message");
      }

      // Update the temporary message with real ID
      setConversationMessages(prev => 
        prev.map(msg => 
          msg.id === tempMessage.id 
            ? { ...msg, id: data.messageId }
            : msg
        )
      );
      
      // Refresh threads to update last message
      await fetchThreads();
      
    } catch (err) {
      console.error("Error sending reply:", err);
      setMessageStatus({ type: "error", message: err.message });
    } finally {
      setSendingMessage(false);
    }
  };

  // Go back to inbox
  const handleBackToInbox = () => {
    setActiveConversation(null);
    setConversationMessages([]);
    setNewMessage("");
  };

  // Utility functions
  const getRoleName = (roleId) => {
    const role = roleId?.toString();
    return role === "1" ? "Admin" : role === "2" ? "Sub-Admin" : "Student";
  };

  const getRoleColor = (roleId) => {
    const role = roleId?.toString();
    return role === "1" 
      ? "bg-purple-100 text-purple-700" 
      : role === "2" 
      ? "bg-blue-100 text-blue-700"
      : "bg-green-100 text-green-700";
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Clear status message after 5 seconds
  useEffect(() => {
    if (messageStatus) {
      const timer = setTimeout(() => setMessageStatus(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [messageStatus]);

  // Conversation View
  if (activeConversation) {
    const currentThread = threads.find(t => t.id === activeConversation);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        <div className="max-w-5xl mx-auto p-4 sm:p-6">
          <div className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl sm:shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden h-[80vh] flex flex-col">
            
            {/* Conversation Header */}
            <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 p-4 sm:p-6 flex items-center gap-4">
              <button
                onClick={handleBackToInbox}
                className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                {currentThread?.recipient_firstName?.charAt(0) || "?"}
              </div>
              
              <div className="flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-white">
                  {currentThread?.recipient_firstName} {currentThread?.recipient_lastName}
                </h2>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold bg-white/20 text-white`}>
                    {getRoleName(currentThread?.recipient_roleId)}
                  </span>
                  <span className="text-white/80 text-sm">Online</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors duration-200">
                  <Phone className="w-5 h-5 text-white" />
                </button>
                <button className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors duration-200">
                  <Video className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading messages...</span>
                </div>
              ) : conversationMessages.length === 0 ? (
                <div className="text-center py-8">
                  <div className="p-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl inline-block mb-4">
                    <MessageSquare className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg">No messages yet</p>
                  <p className="text-gray-400 text-sm mt-2">Start the conversation!</p>
                </div>
              ) : (
                <>
                  {conversationMessages.map((msg, index) => {
                    const isMyMessage = msg.message_from === student.id;
                    const showDate = index === 0 || 
                      formatDate(msg.created_at) !== formatDate(conversationMessages[index - 1]?.created_at);
                    
                    return (
                      <div key={msg.id}>
                        {/* Date separator */}
                        {showDate && (
                          <div className="flex justify-center my-4">
                            <span className="bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full">
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        )}
                        
                        {/* Message bubble */}
                        <div className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'} mb-3`}>
                          <div className="flex items-end gap-2 max-w-xs sm:max-w-md">
                            {!isMyMessage && (
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {msg.sender_firstName?.charAt(0) || "?"}
                              </div>
                            )}
                            
                            <div className={`relative px-4 py-3 rounded-2xl ${
                              isMyMessage 
                                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                                : 'bg-white border border-gray-200 text-gray-800'
                            }`}>
                              {!isMyMessage && (
                                <div className="text-xs font-semibold mb-1 opacity-70">
                                  {msg.sender_firstName}
                                </div>
                              )}
                              <div className="text-sm sm:text-base">{msg.message}</div>
                              <div className={`text-xs mt-1 ${
                                isMyMessage ? 'text-white/70' : 'text-gray-500'
                              }`}>
                                {formatTime(msg.created_at)}
                              </div>
                            </div>
                            
                            {isMyMessage && (
                              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {student.firstName?.charAt(0) || "S"}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <div className="border-t border-gray-200/50 p-4 sm:p-6 bg-white/50 backdrop-blur-sm">
              <div className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    disabled={sendingMessage}
                    className="w-full bg-white/80 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-200 focus:border-blue-300 outline-none transition-all duration-300 resize-none disabled:opacity-50"
                    rows="2"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendConversationMessage();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handleSendConversationMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white p-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingMessage ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main messages view (inbox/new message)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="backdrop-blur-xl bg-white/80 border border-white/20 shadow-xl sm:shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 p-4 sm:p-8">
            <div className="flex items-center gap-3">
              <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">Messages</h1>
                <p className="text-white/80 text-sm sm:text-base mt-0.5">
                  Connect with administrators
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white/50 backdrop-blur-sm border-b border-gray-200/50">
            <div className="flex flex-col sm:flex-row">
              <button
                onClick={() => setActiveTab("inbox")}
                className={`flex items-center justify-center gap-2 px-4 py-4 sm:px-8 sm:py-6 text-sm font-semibold transition-all duration-300 relative ${
                  activeTab === "inbox"
                    ? "text-blue-600 bg-white/60"
                    : "text-gray-600 hover:text-blue-600 hover:bg-white/30"
                }`}
              >
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Inbox</span>
                {activeTab === "inbox" && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-t-full" />
                )}
              </button>
              <button
                onClick={() => setActiveTab("new")}
                className={`flex items-center justify-center gap-2 px-4 py-4 sm:px-8 sm:py-6 text-sm font-semibold transition-all duration-300 relative ${
                  activeTab === "new"
                    ? "text-blue-600 bg-white/60"
                    : "text-gray-600 hover:text-blue-600 hover:bg-white/30"
                }`}
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>New Message</span>
                {activeTab === "new" && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-t-full" />
                )}
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-8">
            {/* Status Messages */}
            {messageStatus && (
              <div
                className={`p-3 sm:p-4 rounded-lg sm:rounded-2xl mb-4 sm:mb-6 border backdrop-blur-sm transition-all duration-500 transform ${
                  messageStatus.type === "success"
                    ? "bg-emerald-50/80 text-emerald-700 border-emerald-200/50 shadow-emerald-100/50"
                    : "bg-red-50/80 text-red-700 border-red-200/50 shadow-red-100/50"
                } shadow-md sm:shadow-lg`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${messageStatus.type === "success" ? "bg-emerald-500" : "bg-red-500"}`} />
                  <span className="text-sm sm:text-base">{messageStatus.message}</span>
                </div>
              </div>
            )}

            {/* Inbox Tab */}
            {activeTab === "inbox" && (
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-gradient-to-br from-white/60 to-white/40 backdrop-blur-xl rounded-xl sm:rounded-3xl border border-white/30 shadow-lg p-4 sm:p-8">
                  <div className="flex items-center gap-3 mb-4 sm:mb-6">
                    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg sm:rounded-xl">
                      <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                      Your Conversations
                    </h3>
                  </div>
                  
                  {loadingThreads ? (
                    <div className="flex items-center justify-center py-8 sm:py-12">
                      <div className="animate-spin rounded-full h-7 w-7 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-gray-600 text-sm sm:text-base">Loading conversations...</span>
                    </div>
                  ) : threads.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <div className="p-3 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl inline-block mb-4">
                        <MessageSquare className="w-10 h-10 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-base sm:text-lg">No conversations yet</p>
                      <p className="text-gray-400 text-xs sm:text-sm mt-2">
                        Start a new conversation to connect with administrators
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      {threads.slice(0, visibleThreadsCount).map((thread) => (
                        <div
                          key={thread.id}
                          onClick={() => handleThreadClick(thread.id)}
                          className="group p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-r from-white/70 to-white/50 backdrop-blur-sm border border-white/40 hover:from-white/90 hover:to-white/70 hover:shadow-lg cursor-pointer transition-all duration-300"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white font-bold text-base sm:text-lg relative">
                                {thread.recipient_firstName?.charAt(0) || "?"}
                                {!thread.is_read && (
                                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                                  <span className={`font-bold text-gray-800 text-base sm:text-lg truncate ${!thread.is_read ? 'text-gray-900' : ''}`}>
                                    {thread.recipient_firstName} {thread.recipient_lastName}
                                  </span>
                                  <span className={`px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-semibold ${getRoleColor(thread.recipient_roleId)}`}>
                                    {getRoleName(thread.recipient_roleId)}
                                  </span>
                                </div>
                                <p className={`mt-1 text-sm sm:text-base line-clamp-2 ${!thread.is_read ? 'text-gray-700 font-medium' : 'text-gray-600'}`}>
                                  {thread.last_message || "No messages yet"}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400 text-xs sm:text-sm ml-12 sm:ml-0">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="font-medium">
                                {thread.updated_at
                                  ? formatDate(thread.updated_at)
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {threads.length > visibleThreadsCount && (
                        <div className="flex justify-center pt-3">
                          <button
                            onClick={() => setVisibleThreadsCount(prev => prev + 5)}
                            className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-gray-100 to-gray-50 hover:from-blue-50 hover:to-cyan-50 text-gray-700 hover:text-blue-600 font-semibold rounded-xl sm:rounded-2xl border border-gray-200 hover:border-blue-200 transition-all duration-300 shadow-md hover:shadow-lg text-sm sm:text-base\"
                          >
                            Show More ({threads.length - visibleThreadsCount})
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* New Message Tab */}
            {activeTab === "new" && (
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-gradient-to-br from-white/60 to-white/40 backdrop-blur-xl rounded-xl sm:rounded-3xl border border-white/30 shadow-lg p-4 sm:p-8">
                  <div className="flex items-center gap-3 mb-4 sm:mb-8">
                    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg sm:rounded-xl">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                      Compose New Message
                    </h3>
                  </div>

                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Select Recipient
                      </label>
                      <div className="relative">
                        <select
                          value={selectedRecipient}
                          onChange={(e) => setSelectedRecipient(e.target.value)}
                          disabled={sendingMessage}
                          className="w-full bg-gradient-to-r from-white/80 to-white/60 backdrop-blur-sm border border-white/50 rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-4 text-gray-800 focus:ring-2 sm:focus:ring-4 focus:ring-blue-200/50 focus:border-blue-300 outline-none transition-all duration-300 appearance-none text-base sm:text-lg shadow-md disabled:opacity-50\"
                        >
                          <option value="">Choose an administrator...</option>
                          {recipients.map((recipient) => (
                            <option key={recipient.id} value={recipient.id}>
                              {recipient.firstName} {recipient.lastName} ({getRoleName(recipient.roleId)})
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Your Message
                      </label>
                      <textarea
                        className="w-full bg-gradient-to-br from-white/80 to-white/60 backdrop-blur-sm border border-white/50 rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-4 text-gray-800 focus:ring-2 sm:focus:ring-4 focus:ring-blue-200/50 focus:border-blue-300 outline-none transition-all duration-300 resize-none shadow-md text-base sm:text-lg disabled:opacity-50\"
                        rows="4"
                        placeholder="Type your message here..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sendingMessage}
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={handleSendMessage}
                        disabled={!selectedRecipient || !message.trim() || sendingMessage}
                        className="group bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 hover:from-blue-700 hover:via-cyan-700 hover:to-blue-700 text-white font-bold px-6 py-3 sm:px-10 sm:py-4 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="text-sm sm:text-base">
                          {sendingMessage ? "Sending..." : "Send Message"}
                        </span>
                        {sendingMessage ? (
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-6 sm:w-6 border-b-2 border-white"></div>
                        ) : (
                          <Send className="w-4 h-4 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300 text-white" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}